/**
 * Blog post loading utilities
 *
 * Uses Vite's import.meta.glob for build-time static analysis of MDX files
 */

import type { BlogPost, BlogFrontmatter, CategorySlug } from "./types";

// Eagerly load all MDX files at build time
const postModules = import.meta.glob<{
  default: React.ComponentType;
  frontmatter: BlogFrontmatter;
}>("/src/content/blog/**/*.mdx", { eager: true });

/**
 * Get all published blog posts, sorted by date (newest first)
 */
export function getAllPosts(): BlogPost[] {
  const posts = Object.entries(postModules)
    .map(([path, module]) => {
      // Extract category and slug from path
      // e.g., "/src/content/blog/early-coding/my-post.mdx" -> category: "early-coding", slug: "my-post"
      const match = path.match(/\/content\/blog\/([^/]+)\/([^/]+)\.mdx$/);
      if (!match) return null;

      const [, category, slug] = match;
      const frontmatter = module?.frontmatter;

      // Skip posts with missing frontmatter
      if (!frontmatter) {
        console.warn(`Missing frontmatter for blog post: ${path}`);
        return null;
      }

      // Only include published posts in production
      if (import.meta.env.PROD && !frontmatter.published) {
        return null;
      }

      return {
        slug: slug!,
        category: category as CategorySlug,
        frontmatter,
        Content: module.default,
      };
    })
    .filter((post): post is BlogPost => post !== null)
    .sort(
      (a, b) =>
        new Date(b.frontmatter.date).getTime() -
        new Date(a.frontmatter.date).getTime()
    );

  return posts;
}

/**
 * Get posts filtered by category
 */
export function getPostsByCategory(category: CategorySlug): BlogPost[] {
  return getAllPosts().filter((post) => post.category === category);
}

/**
 * Get a single post by category and slug
 */
export function getPost(
  category: string,
  slug: string
): BlogPost | undefined {
  return getAllPosts().find(
    (post) => post.category === category && post.slug === slug
  );
}

/**
 * Get related posts based on category and tags
 */
export function getRelatedPosts(currentPost: BlogPost, limit = 3): BlogPost[] {
  const allPosts = getAllPosts();

  // Score posts by relevance
  const scoredPosts = allPosts
    .filter((p) => !(p.category === currentPost.category && p.slug === currentPost.slug))
    .map((post) => {
      let score = 0;

      // Same category = +2 points
      if (post.category === currentPost.category) {
        score += 2;
      }

      // Matching tags = +1 point each
      const matchingTags = post.frontmatter.tags.filter((tag) =>
        currentPost.frontmatter.tags.includes(tag)
      );
      score += matchingTags.length;

      return { post, score };
    })
    .filter(({ score }) => score > 0)
    .sort((a, b) => b.score - a.score);

  return scoredPosts.slice(0, limit).map(({ post }) => post);
}

/**
 * Get featured posts (most recent from each category)
 */
export function getFeaturedPosts(): BlogPost[] {
  const allPosts = getAllPosts();
  const featured: BlogPost[] = [];
  const seenCategories = new Set<CategorySlug>();

  for (const post of allPosts) {
    if (!seenCategories.has(post.category)) {
      featured.push(post);
      seenCategories.add(post.category);
    }
    if (featured.length >= 4) break;
  }

  return featured;
}
